// CalVer "version status" hook — installed version + cached latest +
// CompareKind from the daily update-channel poll. Drives the prominent
// version line + "up to date" / "patch behind" / "year behind" badge
// in AdminOverview (item 69 of PRODUCT-ROADMAP.md, delivery #77).
//
// Two endpoints under /api/admin/version-status: a GET that reads the
// cache (cheap, called on every page load) and a POST that triggers a
// synchronous PollOnce (called by the "Check for updates now" button).

import useSWR from 'swr';
import { parseErrorResponse } from './problem';

export type CompareKind =
  | 'unknown'
  | 'up_to_date'
  | 'patch_behind'
  | 'major_behind'
  | 'year_behind'
  | 'ahead';

export interface VersionStatus {
  installed: string;
  installed_is_calver: boolean;
  build_time: string;
  commit: string;

  enabled: boolean;
  disabled_reason?: string;

  latest_version?: string;
  latest_released_at?: string;
  latest_channel?: string;
  latest_checked_at?: string;
  last_error?: string;

  compare: CompareKind;
}

export interface VersionStatusSettings {
  manifest_url: string;
  updated_at?: string;
  updated_by?: string;
}

const fetcher = async (url: string) => {
  const r = await fetch(url, { credentials: 'include' });
  if (!r.ok) throw await parseErrorResponse(r);
  return r.json();
};

// One refresh per minute on focus revalidation is fine — the daily
// poll is what actually updates the cache; this just keeps a tab
// that's been open for hours from rendering yesterday's view.
export function useVersionStatus() {
  return useSWR<VersionStatus>('/api/admin/version-status', fetcher, {
    revalidateOnFocus: true,
    dedupingInterval: 60_000,
  });
}

export function useVersionStatusSettings() {
  return useSWR<VersionStatusSettings>(
    '/api/admin/version-status/settings',
    fetcher,
    { revalidateOnFocus: false },
  );
}

// Synchronously triggers a fresh poll against the manifest URL.
// Returns the refreshed VersionStatus so the caller can re-render
// without waiting for SWR's next revalidation cycle.
export async function checkForUpdatesNow(): Promise<VersionStatus> {
  const res = await fetch('/api/admin/version-status/check', {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) throw await parseErrorResponse(res);
  return res.json();
}

export async function setManifestURL(manifest_url: string): Promise<void> {
  const res = await fetch('/api/admin/version-status/settings', {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ manifest_url }),
  });
  if (!res.ok) throw await parseErrorResponse(res);
}
